import Loader from './parts/Loader.js'

new Loader({})

// ===========================================================================
// ヘッダー
// ===========================================================================

let lastScrollY = window.scrollY
let showTimer: ReturnType<typeof setTimeout> | null = null

const header = document.querySelector<HTMLElement>('.site-header')

if (header) {
  const showHeader = () => {
    header.classList.remove('is-hidden')

    if (showTimer) {
      clearTimeout(showTimer)
      showTimer = null
    }
  }

  const hideHeader = () => {
    header.classList.add('is-hidden')
  }

  const scheduleShowHeader = () => {
    if (showTimer) {
      clearTimeout(showTimer)
    }

    showTimer = setTimeout(() => {
      showHeader()
    }, 3000)
  }

  window.addEventListener('scroll', () => {
    const currentScrollY = window.scrollY

    if (currentScrollY <= 0) {
      showHeader()
      lastScrollY = currentScrollY
      return
    }

    if (currentScrollY > lastScrollY) {
      if (!header.classList.contains('is-hidden')) {
        hideHeader()
      }

      scheduleShowHeader()
    }

    if (currentScrollY < lastScrollY) {
      if (header.classList.contains('is-hidden')) {
        showHeader()
      }
    }

    lastScrollY = currentScrollY
  })
}

// =====================================================================
// 車両画像ギャラリー
// car-catalog-item の画像をクリックしてモーダル表示
// =====================================================================
class CarGallery {
  private items: NodeListOf<HTMLElement>
  private modal: HTMLElement | null
  private modalImage: HTMLImageElement | null
  private modalCurrent: HTMLElement | null
  private modalTotal: HTMLElement | null
  private closeButton: HTMLButtonElement | null
  private prevButton: HTMLButtonElement | null
  private nextButton: HTMLButtonElement | null
  private overlay: HTMLElement | null

  private images: string[] = []
  private currentIndex = 0

  private touchStartX = 0
  private touchEndX = 0

  constructor() {
    this.items = document.querySelectorAll<HTMLElement>('.car-catalog-item')

    this.modal = document.querySelector<HTMLElement>('.car-gallery-modal')

    if (!this.modal || !this.items.length) {
      return
    }

    this.modalImage = this.modal.querySelector<HTMLImageElement>(
      '.car-gallery-modal-image img',
    )

    this.modalCurrent = this.modal.querySelector<HTMLElement>(
      '.car-gallery-modal-current',
    )

    this.modalTotal = this.modal.querySelector<HTMLElement>(
      '.car-gallery-modal-total',
    )

    this.closeButton = this.modal.querySelector<HTMLButtonElement>(
      '.car-gallery-modal-close',
    )

    this.prevButton = this.modal.querySelector<HTMLButtonElement>(
      '.car-gallery-modal-prev',
    )

    this.nextButton = this.modal.querySelector<HTMLButtonElement>(
      '.car-gallery-modal-next',
    )

    this.overlay = this.modal.querySelector<HTMLElement>(
      '.car-gallery-modal-overlay',
    )

    this.bindEvents()
  }

  // ===================================================================
  // イベント
  // ===================================================================
  private bindEvents(): void {
    // --------------------------------------------------
    // フロント画像
    // --------------------------------------------------
    this.items.forEach((item) => {
      const triggers = item.querySelectorAll<HTMLElement>(
        '.car-gallery-trigger',
      )

      triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          this.openModal(item, trigger)
        })
      })
    })

    // --------------------------------------------------
    // 閉じる
    // --------------------------------------------------
    this.closeButton?.addEventListener('click', () => {
      this.closeModal()
    })

    // --------------------------------------------------
    // 背景クリック
    // --------------------------------------------------
    this.overlay?.addEventListener('click', () => {
      this.closeModal()
    })

    // --------------------------------------------------
    // 前へ
    // --------------------------------------------------
    this.prevButton?.addEventListener('click', () => {
      this.showPrevious()
    })

    // --------------------------------------------------
    // 次へ
    // --------------------------------------------------
    this.nextButton?.addEventListener('click', () => {
      this.showNext()
    })

    // --------------------------------------------------
    // ESC
    // --------------------------------------------------
    document.addEventListener('keydown', (event) => {
      if (!this.modal?.classList.contains('is-open')) {
        return
      }

      if (event.key === 'Escape') {
        this.closeModal()
      }

      if (event.key === 'ArrowLeft') {
        this.showPrevious()
      }

      if (event.key === 'ArrowRight') {
        this.showNext()
      }
    })

    // --------------------------------------------------
    // スワイプ
    // --------------------------------------------------
    this.modal.addEventListener(
      'touchstart',
      (event) => {
        this.touchStartX = event.touches[0]?.clientX ?? 0
      },
      { passive: true },
    )

    this.modal.addEventListener(
      'touchend',
      (event) => {
        this.touchEndX = event.changedTouches[0]?.clientX ?? 0

        this.handleSwipe()
      },
      { passive: true },
    )
  }

  // ===================================================================
  // モーダルを開く
  // ===================================================================
  private openModal(item: HTMLElement, trigger: HTMLElement): void {
    const galleryData = item.dataset.gallery

    if (!galleryData) {
      return
    }

    try {
      const images = JSON.parse(galleryData)

      if (!Array.isArray(images) || !images.length) {
        return
      }

      this.images = images.filter(
        (image): image is string =>
          typeof image === 'string' && image.length > 0,
      )
    } catch (error) {
      console.error('車両画像ギャラリーの読み込みに失敗しました。', error)

      return
    }

    if (!this.images.length) {
      return
    }

    // クリックした画像の位置
    const clickedIndex = Number(trigger.dataset.galleryIndex ?? 0)

    this.currentIndex =
      clickedIndex >= 0 && clickedIndex < this.images.length ? clickedIndex : 0

    this.updateImage()

    this.modal?.classList.add('is-open')
    this.modal?.setAttribute('aria-hidden', 'false')

    document.body.classList.add('is-car-gallery-open')
  }

  // ===================================================================
  // モーダルを閉じる
  // ===================================================================
  private closeModal(): void {
    if (!this.modal) {
      return
    }

    this.modal.classList.remove('is-open')
    this.modal.setAttribute('aria-hidden', 'true')

    document.body.classList.remove('is-car-gallery-open')

    this.images = []
    this.currentIndex = 0
  }

  // ===================================================================
  // 画像更新
  // ===================================================================
  private updateImage(): void {
    if (!this.modalImage || !this.images.length) {
      return
    }

    const image = this.images[this.currentIndex]

    if (!image) {
      return
    }

    this.modalImage.src = image
    this.modalImage.alt = `車両画像 ${this.currentIndex + 1}`

    if (this.modalCurrent) {
      this.modalCurrent.textContent = String(this.currentIndex + 1)
    }

    if (this.modalTotal) {
      this.modalTotal.textContent = String(this.images.length)
    }
  }

  // ===================================================================
  // 前の画像
  // ===================================================================
  private showPrevious(): void {
    if (!this.images.length) {
      return
    }

    this.currentIndex--

    if (this.currentIndex < 0) {
      this.currentIndex = this.images.length - 1
    }

    this.updateImage()
  }

  // ===================================================================
  // 次の画像
  // ===================================================================
  private showNext(): void {
    if (!this.images.length) {
      return
    }

    this.currentIndex++

    if (this.currentIndex >= this.images.length) {
      this.currentIndex = 0
    }

    this.updateImage()
  }

  // ===================================================================
  // スワイプ判定
  // ===================================================================
  private handleSwipe(): void {
    const distance = this.touchEndX - this.touchStartX

    // 小さい移動は無視
    if (Math.abs(distance) < 50) {
      return
    }

    if (distance > 0) {
      this.showPrevious()
    } else {
      this.showNext()
    }
  }
}

// =====================================================================
// 車両画像ギャラリー起動
// =====================================================================
new CarGallery()

// ===========================================================================
// モバイルナビ
// ===========================================================================

const menuButton = document.querySelector<HTMLButtonElement>('.header-menu')

const mobileNav = document.querySelector<HTMLElement>('.header-nav--sp')

if (menuButton && mobileNav) {
  // -------------------------------------------------------------------------
  // ハンバーガーメニュー開閉
  // -------------------------------------------------------------------------

  menuButton.addEventListener('click', () => {
    menuButton.classList.toggle('is-active')
    mobileNav.classList.toggle('is-active')
  })

  // -------------------------------------------------------------------------
  // クリックで閉じる
  // -------------------------------------------------------------------------

  const navLinks = mobileNav.querySelectorAll<HTMLAnchorElement>('a')

  navLinks.forEach((link) => {
    link.addEventListener('click', () => {
      menuButton.classList.remove('is-active')
      mobileNav.classList.remove('is-active')
    })
  })
}

// ===========================================================================
// スムーススクロール
// ===========================================================================

document.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach((link) => {
  link.addEventListener('click', (e) => {
    const targetId = link.getAttribute('href')

    if (!targetId || targetId === '#') {
      return
    }

    const target = document.querySelector<HTMLElement>(targetId)

    if (!target) {
      return
    }

    e.preventDefault()

    const header = document.querySelector<HTMLElement>('.site-header')

    const headerHeight = header?.offsetHeight ?? 0

    const targetTop =
      target.getBoundingClientRect().top + window.scrollY - headerHeight

    window.scrollTo({
      top: targetTop,
      behavior: 'smooth',
    })
  })
})
// ==========================================================================
// 無限スクロールギャラリー
// ==========================================================================

function initInfiniteGallery(): void {
  const galleries = document.querySelectorAll<HTMLElement>('.infinite-gallery')

  galleries.forEach((gallery) => {
    const viewport = gallery.querySelector<HTMLElement>(
      '.infinite-gallery-viewport',
    )

    const track = gallery.querySelector<HTMLElement>('.infinite-gallery-track')

    const prevButton = gallery.querySelector<HTMLButtonElement>(
      '.infinite-gallery-button--prev',
    )

    const nextButton = gallery.querySelector<HTMLButtonElement>(
      '.infinite-gallery-button--next',
    )

    if (!viewport || !track || !prevButton || !nextButton) {
      return
    }

    const originalSlides = Array.from(
      track.querySelectorAll<HTMLElement>('.infinite-gallery-slide'),
    )

    if (originalSlides.length === 0) {
      return
    }

    // --------------------------------------------------
    // 無限ループ用に2セット複製
    // 元画像6枚 + 複製12枚 = 合計18枚
    // --------------------------------------------------
    for (let copy = 0; copy < 2; copy++) {
      originalSlides.forEach((slide) => {
        const clone = slide.cloneNode(true) as HTMLElement

        clone.setAttribute('aria-hidden', 'true')

        track.appendChild(clone)
      })
    }

    // 1セット目の終端にある複製画像
    const firstClone = track.children[originalSlides.length] as HTMLElement

    let loopWidth = 0
    let currentX = 0
    let animationId: number | null = null

    let isPaused = false
    let isDragging = false

    let startPointerX = 0
    let startTranslateX = 0

    const speed = 0.5

    // --------------------------------------------------
    // ループ幅を更新
    // --------------------------------------------------
    const updateLoopWidth = (): void => {
      if (!firstClone) {
        return
      }

      loopWidth = firstClone.offsetLeft
    }

    // --------------------------------------------------
    // 現在位置をループ範囲内に補正
    // --------------------------------------------------
    const normalizePosition = (): void => {
      if (loopWidth <= 0) {
        return
      }

      // 左方向に進みすぎた場合
      while (currentX <= -loopWidth) {
        currentX += loopWidth
      }

      // 右方向に進みすぎた場合
      while (currentX > 0) {
        currentX -= loopWidth
      }
    }

    // --------------------------------------------------
    // transform適用
    // --------------------------------------------------
    const setTransform = (): void => {
      track.style.transform = `translate3d(${currentX}px, 0, 0)`
    }

    // --------------------------------------------------
    // 移動
    // --------------------------------------------------
    const moveBy = (distance: number): void => {
      currentX -= distance

      normalizePosition()
      setTransform()
    }

    // --------------------------------------------------
    // 自動スクロール
    // --------------------------------------------------
    const animate = (): void => {
      if (!isPaused && !isDragging) {
        currentX -= speed

        normalizePosition()
        setTransform()
      }

      animationId = window.requestAnimationFrame(animate)
    }

    const startAnimation = (): void => {
      if (animationId === null) {
        animationId = window.requestAnimationFrame(animate)
      }
    }

    const stopAnimation = (): void => {
      if (animationId !== null) {
        window.cancelAnimationFrame(animationId)
        animationId = null
      }
    }

    // --------------------------------------------------
    // Prevボタン
    // --------------------------------------------------
    prevButton.addEventListener('click', () => {
      isPaused = true

      moveBy(-loopWidth / originalSlides.length)

      window.setTimeout(() => {
        isPaused = false
      }, 800)
    })

    // --------------------------------------------------
    // Nextボタン
    // --------------------------------------------------
    nextButton.addEventListener('click', () => {
      isPaused = true

      moveBy(loopWidth / originalSlides.length)

      window.setTimeout(() => {
        isPaused = false
      }, 800)
    })

    // --------------------------------------------------
    // ドラッグ開始
    // --------------------------------------------------
    viewport.addEventListener('pointerdown', (event: PointerEvent) => {
      isDragging = true
      isPaused = true

      startPointerX = event.clientX
      startTranslateX = currentX

      viewport.classList.add('is-dragging')

      viewport.setPointerCapture(event.pointerId)
    })

    // --------------------------------------------------
    // ドラッグ中
    // --------------------------------------------------
    viewport.addEventListener('pointermove', (event: PointerEvent) => {
      if (!isDragging) {
        return
      }

      const distance = event.clientX - startPointerX

      currentX = startTranslateX + distance

      normalizePosition()
      setTransform()
    })

    // --------------------------------------------------
    // ドラッグ終了
    // --------------------------------------------------
    const finishDragging = (): void => {
      if (!isDragging) {
        return
      }

      isDragging = false

      viewport.classList.remove('is-dragging')

      window.setTimeout(() => {
        isPaused = false
      }, 500)
    }

    viewport.addEventListener('pointerup', finishDragging)

    viewport.addEventListener('pointercancel', finishDragging)

    viewport.addEventListener('lostpointercapture', finishDragging)

    // --------------------------------------------------
    // リサイズ時の補正
    // --------------------------------------------------
    let resizeTimer: number | null = null

    const handleResize = (): void => {
      if (resizeTimer !== null) {
        window.cancelAnimationFrame(resizeTimer)
      }

      resizeTimer = window.requestAnimationFrame(() => {
        updateLoopWidth()

        normalizePosition()
        setTransform()

        resizeTimer = null
      })
    }

    window.addEventListener('resize', handleResize)

    // --------------------------------------------------
    // 画像読み込み後にも幅を再計算
    // --------------------------------------------------
    const images = track.querySelectorAll<HTMLImageElement>('img')

    images.forEach((image) => {
      image.addEventListener('load', () => {
        updateLoopWidth()

        normalizePosition()
        setTransform()
      })
    })

    // --------------------------------------------------
    // タブ切り替え時などの負荷対策
    // --------------------------------------------------
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        isPaused = true

        stopAnimation()
      } else {
        isPaused = false

        startAnimation()
      }
    })

    // --------------------------------------------------
    // 初期化
    // --------------------------------------------------
    updateLoopWidth()
    normalizePosition()
    setTransform()

    startAnimation()
  })
}

initInfiniteGallery()
// ===========================================================================
// 旧：車両自動スライダー
//
// 現在は使用していない。
// 将来スライダー仕様に戻す場合は、このコメントアウトを外す。
// ===========================================================================

/*

const carSlider =
  document.querySelector<HTMLElement>(
    '.car-slider',
  )

const track =
  carSlider?.querySelector<HTMLElement>(
    '.car-slider-track',
  )

const firstGroup =
  carSlider?.querySelector<HTMLElement>(
    '.car-slider-group',
  )

const prevBtn =
  carSlider?.querySelector<HTMLButtonElement>(
    '.car-slider-prev',
  )

const nextBtn =
  carSlider?.querySelector<HTMLButtonElement>(
    '.car-slider-next',
  )


const modal =
  document.querySelector<HTMLElement>(
    '.car-modal',
  )

const modalOverlay =
  document.querySelector<HTMLElement>(
    '.car-modal-overlay',
  )

const modalClose =
  document.querySelector<HTMLButtonElement>(
    '.car-modal-close',
  )

const modalImage =
  document.querySelector<HTMLImageElement>(
    '.car-modal-image img',
  )

const modalTitle =
  document.querySelector<HTMLElement>(
    '.car-modal-title',
  )

const modalYear =
  document.querySelector<HTMLElement>(
    '.car-modal-year',
  )

const modalPrice =
  document.querySelector<HTMLElement>(
    '.car-modal-price',
  )


if (
  carSlider &&
  track &&
  firstGroup
) {

  let positionX = 0

  const speed = 1.2

  let isHovered = false
  let isDragging = false
  let isModalOpen = false

  let startX = 0
  let currentX = 0
  let hasMoved = false


  const updateLoop = () => {

    if (
      !isHovered &&
      !isDragging &&
      !isModalOpen
    ) {
      positionX -= speed
    }


    const groupWidth =
      firstGroup.offsetWidth


    if (
      positionX <= -groupWidth
    ) {

      positionX += groupWidth

    } else if (
      positionX > 0
    ) {

      positionX -= groupWidth

    }


    track.style.transform =
      `translate3d(${positionX}px, 0, 0)`


    requestAnimationFrame(
      updateLoop,
    )
  }


  requestAnimationFrame(
    updateLoop,
  )


  carSlider.addEventListener(
    'mouseenter',
    () => {
      isHovered = true
    },
  )


  carSlider.addEventListener(
    'mouseleave',
    () => {
      isHovered = false
      isDragging = false
    },
  )


  carSlider.addEventListener(
    'pointerdown',
    (e: PointerEvent) => {

      const target =
        e.target as HTMLElement

      if (
        target.closest(
          '.car-slider-nav',
        )
      ) {
        return
      }


      isDragging = true
      hasMoved = false

      startX = e.clientX
      currentX = positionX
    },
  )


  window.addEventListener(
    'pointermove',
    (e: PointerEvent) => {

      if (!isDragging) {
        return
      }


      const diff =
        e.clientX - startX


      if (
        Math.abs(diff) > 5
      ) {
        hasMoved = true
      }


      positionX =
        currentX + diff
    },
  )


  window.addEventListener(
    'pointerup',
    () => {
      isDragging = false
    },
  )


  // ボタンによる送り処理
  const getStepWidth = () => {

    const item =
      firstGroup.querySelector<HTMLElement>(
        '.car-slider-item',
      )

    return item
      ? item.offsetWidth + 20
      : 260
  }


  if (prevBtn) {

    prevBtn.addEventListener(
      'click',
      (e) => {

        e.stopPropagation()

        positionX +=
          getStepWidth()
      },
    )
  }


  if (nextBtn) {

    nextBtn.addEventListener(
      'click',
      (e) => {

        e.stopPropagation()

        positionX -=
          getStepWidth()
      },
    )
  }


  if (
    modal &&
    modalOverlay &&
    modalClose &&
    modalImage &&
    modalTitle &&
    modalYear &&
    modalPrice
  ) {

    const openModal =
      (item: HTMLElement) => {

        const image =
          item.querySelector<HTMLImageElement>(
            '.car-slider-item-image img',
          )


        if (!image) {
          return
        }


        isModalOpen = true


        modalImage.src =
          image.currentSrc ||
          image.src

        modalImage.alt =
          item.dataset.title ?? ''

        modalTitle.textContent =
          item.dataset.title ?? ''

        modalYear.textContent =
          item.dataset.year ?? ''

        modalPrice.textContent =
          item.dataset.price ?? ''


        modal.classList.add(
          'is-open',
        )

        modal.setAttribute(
          'aria-hidden',
          'false',
        )

        document.body.style.overflow =
          'hidden'
      }


    const closeModal = () => {

      modal.classList.remove(
        'is-open',
      )

      modal.setAttribute(
        'aria-hidden',
        'true',
      )

      document.body.style.overflow =
        ''

      isModalOpen = false
    }


    carSlider.addEventListener(
      'click',
      (event) => {

        if (hasMoved) {
          return
        }


        const target =
          event.target as HTMLElement

        const button =
          target.closest<HTMLButtonElement>(
            '.car-slider-item-button',
          )


        if (button) {

          const item =
            button.closest<HTMLElement>(
              '.car-slider-item',
            )


          if (item) {
            openModal(item)
          }
        }
      },
    )


    modalClose.addEventListener(
      'click',
      closeModal,
    )

    modalOverlay.addEventListener(
      'click',
      closeModal,
    )


    document.addEventListener(
      'keydown',
      (event) => {

        if (
          event.key === 'Escape' &&
          modal.classList.contains(
            'is-open',
          )
        ) {
          closeModal()
        }
      },
    )
  }
}

*/
// // ===========================================================================
// // 車両一覧
// // ACF car-category / vehicle-year / vehicle-price 対応
// // ===========================================================================

// class Car {
//   private section: HTMLElement | null
//   private tabs: NodeListOf<HTMLButtonElement>
//   private cards: NodeListOf<HTMLElement>
//   private modal: HTMLElement | null
//   private modalImage: HTMLImageElement | null
//   private modalTitle: HTMLElement | null
//   private modalYear: HTMLElement | null
//   private modalPrice: HTMLElement | null

//   constructor() {
//     this.section = document.querySelector<HTMLElement>('.car-section')

//     if (!this.section) {
//       return
//     }

//     this.tabs = this.section.querySelectorAll<HTMLButtonElement>('.car-tab')

//     // グーネットも含めて取得する
//     // ↓
//     // ただし、後の処理で car-card--goonet を明示的に除外する
//     this.cards = this.section.querySelectorAll<HTMLElement>('.car-card')

//     this.modal = this.section.querySelector<HTMLElement>('.car-modal')

//     this.modalImage = this.section.querySelector<HTMLImageElement>(
//       '.car-modal__image img',
//     )

//     this.modalTitle =
//       this.section.querySelector<HTMLElement>('.car-modal__title')

//     this.modalYear = this.section.querySelector<HTMLElement>(
//       '.car-modal__year dd',
//     )

//     this.modalPrice = this.section.querySelector<HTMLElement>(
//       '.car-modal__price dd',
//     )

//     this.bindEvents()

//     // 初期表示
//     this.switchCategory(
//       this.section.querySelector<HTMLButtonElement>(
//         '.car-tab[data-category="all"]',
//       ) ?? this.tabs[0],
//     )
//   }

//   // =========================================================================
//   // イベント登録
//   // =========================================================================
//   private bindEvents(): void {
//     // -----------------------------------------------------------------------
//     // タブ
//     // -----------------------------------------------------------------------
//     this.tabs.forEach((tab) => {
//       tab.addEventListener('click', () => {
//         this.switchCategory(tab)
//       })
//     })

//     // -----------------------------------------------------------------------
//     // 車両カード
//     // -----------------------------------------------------------------------
//     this.cards.forEach((card) => {
//       // グーネットはモーダル対象外
//       if (card.classList.contains('car-card--goonet')) {
//         return
//       }

//       const button = card.querySelector<HTMLButtonElement>('.car-card__button')

//       button?.addEventListener('click', () => {
//         this.openModal(card)
//       })
//     })

//     // -----------------------------------------------------------------------
//     // モーダル：閉じるボタン
//     // -----------------------------------------------------------------------
//     const closeButton =
//       this.section?.querySelector<HTMLButtonElement>('.car-modal__close')

//     closeButton?.addEventListener('click', () => {
//       this.closeModal()
//     })

//     // -----------------------------------------------------------------------
//     // モーダル：オーバーレイ
//     // -----------------------------------------------------------------------
//     const overlay = this.section?.querySelector<HTMLElement>(
//       '.car-modal__overlay',
//     )

//     overlay?.addEventListener('click', () => {
//       this.closeModal()
//     })

//     // -----------------------------------------------------------------------
//     // モーダル：ESCキー
//     // -----------------------------------------------------------------------
//     document.addEventListener('keydown', (event) => {
//       if (event.key === 'Escape') {
//         this.closeModal()
//       }
//     })
//   }

//   // =========================================================================
//   // カテゴリー切り替え
//   // =========================================================================
//   private switchCategory(activeTab: HTMLButtonElement | undefined): void {
//     if (!activeTab) {
//       return
//     }

//     const category = activeTab.dataset.category

//     if (!category) {
//       return
//     }

//     // -----------------------------------------------------------------------
//     // タブのactive状態
//     // -----------------------------------------------------------------------
//     this.tabs.forEach((tab) => {
//       const isActive = tab === activeTab

//       tab.classList.toggle('is-active', isActive)
//       tab.setAttribute('aria-selected', String(isActive))
//     })

//     // -----------------------------------------------------------------------
//     // 車両表示
//     // -----------------------------------------------------------------------
//     this.cards.forEach((card) => {
//       // --------------------------------------------------
//       // グーネットは常に表示
//       // --------------------------------------------------
//       if (card.classList.contains('car-card--goonet')) {
//         card.classList.remove('is-hidden')
//         return
//       }

//       const categories =
//         card.dataset.categories?.split(' ').filter(Boolean) ?? []

//       // --------------------------------------------------
//       // ALLの場合はすべて表示
//       // --------------------------------------------------
//       if (category === 'all') {
//         card.classList.remove('is-hidden')
//         return
//       }

//       // --------------------------------------------------
//       // 選択したカテゴリーを持っているか
//       // --------------------------------------------------
//       const isVisible = categories.includes(category)

//       card.classList.toggle('is-hidden', !isVisible)
//     })
//   }

//   // =========================================================================
//   // モーダルを開く
//   // =========================================================================
//   private openModal(card: HTMLElement): void {
//     if (!this.modal) {
//       return
//     }

//     const title = card.dataset.title ?? ''
//     const image = card.dataset.image ?? ''
//     const year = card.dataset.year ?? ''
//     const price = card.dataset.price ?? ''

//     // -----------------------------------------------------------------------
//     // データ反映
//     // -----------------------------------------------------------------------
//     if (this.modalTitle) {
//       this.modalTitle.textContent = title
//     }

//     if (this.modalImage) {
//       this.modalImage.src = image
//       this.modalImage.alt = title
//     }

//     if (this.modalYear) {
//       this.modalYear.textContent = year
//     }

//     if (this.modalPrice) {
//       this.modalPrice.textContent = price
//     }

//     // -----------------------------------------------------------------------
//     // モーダル表示
//     // -----------------------------------------------------------------------
//     this.modal.classList.add('is-open')
//     this.modal.setAttribute('aria-hidden', 'false')
//     document.body.classList.add('is-modal-open')
//   }

//   // =========================================================================
//   // モーダルを閉じる
//   // =========================================================================
//   private closeModal(): void {
//     if (!this.modal) {
//       return
//     }

//     this.modal.classList.remove('is-open')
//     this.modal.setAttribute('aria-hidden', 'true')
//     document.body.classList.remove('is-modal-open')
//   }
// }

// // ===========================================================================
// // 車両機能を起動
// // ===========================================================================

// new Car()
